/**
 * BUILDLIST 69.91 (a): a capture point the walk can stand in. The points are laid between the sides with no look at the
 * map's props, and the dressing keeps only its own stands off them (core/biomes.ts) — so a map's rock or tree could stand
 * over a circle. Story-2's p0 (both sides' hold) stood under a tree whose walk footprint, 3.67 people round, came within
 * 1.45 people of the centre: the local steer (core/steer.ts reachableGoal) moved every walk to it out to the tree's
 * clearance, 2.8–5.9 people from the centre against a capture circle of 2.73, and the side's five stood there to the end.
 *
 * At setup, on the action clock, every rock or flora whose walk footprint (a person's, footprintObstacles) reaches into a
 * capture circle — within the capture radius and a body's radius more (the steer keeps a body that far off a prop) — is
 * taken off the map. The points stay where they were; structures are never touched. Pure.
 */
import type { GameState } from '../types';
import { dacos } from './dmath';
import { vecDot, vecNormalize } from '../math';
import { personSetOf } from './scaleProfile';
import { footprintObstacles, personMover } from './hits';
import { initFloraDestructionState } from './floraTracking';

export function clearCapturePoints(state: GameState): GameState {
  const sites = state.modeState.points?.sites ?? [];
  if (state.config.clock !== 'action' || sites.length === 0) return state;
  const ps = personSetOf(state);
  const reach = (state.config.modeConfig.captureRadius ?? ps.captureRadius) + ps.unitRadius;
  const rocks = new Set<number>(), flora = new Set<number>();
  for (const pi of [...new Set(sites.map(s => s.planetIndex))].sort((a, b) => a - b)) {
    const planet = state.planets[pi];
    if (!planet) continue;
    const here = sites.filter(s => s.planetIndex === pi).map(s => vecNormalize(s.surfaceDir));
    for (const o of footprintObstacles(state, pi, personMover(ps)).list) {
      if (o.ref?.kind !== 'rock' && o.ref?.kind !== 'flora') continue;
      const d = vecNormalize(o.dir);
      if (here.some(c => dacos(Math.max(-1, Math.min(1, vecDot(d, c)))) * planet.radius - o.radius < reach)) (o.ref.kind === 'rock' ? rocks : flora).add(o.ref.id);
    }
  }
  if (rocks.size === 0 && flora.size === 0) return state;
  let next: GameState = { ...state };
  if (rocks.size) next.rocks = state.rocks.filter(r => !rocks.has(r.id));
  if (flora.size) {
    next.flora = state.flora.filter(f => !flora.has(f.id));
    if (next.floraDestruction) next = { ...next, floraDestruction: initFloraDestructionState(next.flora) };
  }
  return next;
}
